import { useEffect, useState } from "react";
import { BellRing } from "lucide-react";
import dayjs from "dayjs";
import useApiMethods from "../../../lib/useApiMethods";
import { LEAD_DEMO_REMINDER_URL } from "../../../constants/api";
import { cn } from "../../../utils/tailwindUtils";
import { offsetLabel } from "../../email/DemoReminderCard";

const STATUS = {
    pending: ["Waiting", "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"],
    sending: ["Sending", "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300"],
    sent:    ["Sent", "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"],
    skipped: ["Not sent", "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"],
    failed:  ["Failed", "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"],
};

/**
 * Lead page: the reminders for the booked demo and what the lead answered on
 * the website (confirmed, or moved it). email/demo-reminder/lead/<id>/
 * (platform_core/email_integration/demo_reminder.py). Hidden when there is
 * no booked demo and no answer, or the reminders are off and nothing was sent.
 */
export default function DemoReminderCard({ customerId, stage }) {
    const { fetch } = useApiMethods();
    const [data, setData] = useState(null);

    useEffect(() => {
        let live = true;
        if (!customerId) return undefined;
        fetch(LEAD_DEMO_REMINDER_URL(customerId)).then(res => {
            if (live && res?.status === "success") setData(res.data);
        });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId, stage]);

    if (!data) return null;
    const { reminders = [], responses = [] } = data;
    if (!responses.length && (!data.demo_at || (!data.enabled && !reminders.length))) return null;

    return (
        <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-900/20 p-4">
            <div className="flex items-center gap-1.5 mb-2">
                <BellRing className="h-3.5 w-3.5 text-indigo-700 dark:text-indigo-300" aria-hidden="true" />
                <p className="text-[10px] font-bold text-indigo-700 dark:text-indigo-300 uppercase tracking-widest flex-1">
                    Demo reminder
                </p>
            </div>
            {data.demo_at && (
                <p className="text-xs font-medium text-gray-800 dark:text-gray-100 mb-2">Demo: {data.demo_label}</p>
            )}
            {reminders.length > 0 && (
                <ul className="space-y-1 mb-2">
                    {reminders.map(r => {
                        const [label, cls] = STATUS[r.status] || [r.status, STATUS.pending[1]];
                        return (
                            <li key={`${r.channel}-${r.offset_minutes}`} className="flex items-center gap-1.5 text-[11px] text-gray-600 dark:text-gray-300">
                                <span className="w-16 shrink-0">{r.channel === "whatsapp" ? "WhatsApp" : "Email"}</span>
                                <span className="flex-1 min-w-0 truncate" title={r.reason || ""}>{offsetLabel(r.offset_minutes)}</span>
                                <span className={cn("px-1.5 py-0.5 rounded-full text-[10px] font-medium", cls)}>{label}</span>
                            </li>
                        );
                    })}
                </ul>
            )}
            {data.demo_at && !reminders.length && (
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">No reminders were due for this booking.</p>
            )}
            {responses.length > 0 && (
                <div className="border-t border-indigo-100 dark:border-indigo-800 pt-2 space-y-1.5">
                    {responses.slice(0, 3).map((r, i) => (
                        <div key={i} className="text-[11px]">
                            <span className={cn("font-semibold", r.action === "confirmed" ? "text-green-700 dark:text-green-300" : "text-amber-700 dark:text-amber-300")}>
                                {r.action === "confirmed" ? "Confirmed" : `Moved to ${dayjs(r.new_demo_at).format("ddd D MMM, h:mm A")}`}
                            </span>
                            <span className="text-gray-500 dark:text-gray-400"> · {dayjs(r.created_at).format("D MMM, h:mm A")}</span>
                            {r.note && <p className="text-gray-700 dark:text-gray-200 italic">“{r.note}”</p>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
